import {
  OutboundProcess,
  OutboundShipmentNoteSKU,
  OutboundPickingConfirmationSKU,
  OutboundGoodsReceivedNoteSKU,
  OutboundPlannedPickLine,
} from '@features/supplyChain/types';

type DespatchNote = OutboundProcess['despatchNote'];

export const hasDespatchOrShipmentNote = (process: OutboundProcess): boolean =>
  !!(process.shipmentNote || process.despatchNote);

export const despatchLineCount = (despatchNote: DespatchNote): number =>
  (despatchNote?.skus.length ?? 0) + (despatchNote?.products?.length ?? 0);

export const despatchOrderedQuantity = (despatchNote: DespatchNote): number => {
  const skuQuantity = (despatchNote?.skus ?? []).reduce(
    (sum, sku) => sum + sku.skuQuantity,
    0
  );
  const productQuantity = (despatchNote?.products ?? []).reduce(
    (sum, product) => sum + product.productQuantity,
    0
  );
  return skuQuantity + productQuantity;
};

export const despatchTotalProducts = (despatchNote: DespatchNote): number => {
  const skuProducts = (despatchNote?.skus ?? []).reduce(
    (sum, sku) => sum + sku.skuQuantity * (sku.amountOfProductInSku || 1),
    0
  );
  const lineProducts = (despatchNote?.products ?? []).reduce(
    (sum, product) => sum + product.productQuantity,
    0
  );
  return skuProducts + lineProducts;
};

export const hasProductLines = (despatchNote: DespatchNote): boolean =>
  (despatchNote?.products?.length ?? 0) > 0;

export const plannedWholeUnitsForSku = (
  plannedLines: OutboundPlannedPickLine[] | null | undefined,
  skuId: string
): number | undefined => {
  const linesForSku = (plannedLines ?? []).filter(
    (line) => line.skuId === skuId
  );
  if (linesForSku.length === 0) return undefined;
  return linesForSku.reduce(
    (sum, line) => sum + line.wholeUnitsToPick + line.unitsToBreak,
    0
  );
};

export const skuLinePlanFor = (
  plannedLines: OutboundPlannedPickLine[] | null | undefined,
  skuId: string
): OutboundPlannedPickLine | undefined =>
  (plannedLines ?? []).find((line) => !line.productId && line.skuId === skuId);

export const plannedSkuIdentifiersForProduct = (
  plannedLines: OutboundPlannedPickLine[] | null | undefined,
  productId: string
): string[] =>
  Array.from(
    new Set(
      (plannedLines ?? [])
        .filter((line) => line.productId === productId)
        .map((line) => line.skuIdentifier)
        .filter((identifier): identifier is string => !!identifier)
    )
  );

export const productsFromSkus = (
  skus: Pick<OutboundShipmentNoteSKU, 'skuQuantity' | 'amountOfProductInSku'>[]
): number =>
  skus.reduce(
    (sum, sku) => sum + sku.skuQuantity * (sku.amountOfProductInSku || 1),
    0
  );

export const pickingProductsPicked = (
  skus: OutboundPickingConfirmationSKU[]
): number =>
  skus.reduce(
    (sum, sku) =>
      sum +
      (sku.productsPicked ??
        sku.pickedQuantity * (sku.amountOfProductInSku || 1)),
    0
  );

export const podProductsReceived = (
  goodsReceivedNoteSkus: OutboundGoodsReceivedNoteSKU[],
  shipmentNoteSkus: OutboundShipmentNoteSKU[]
): number =>
  goodsReceivedNoteSkus.reduce((sum, receivedSku) => {
    const shippedSku = shipmentNoteSkus.find(
      (sku) => sku.skuId === receivedSku.skuId
    );
    return (
      sum + receivedSku.receivedSkuQuantity * (shippedSku?.amountOfProductInSku || 1)
    );
  }, 0);
